import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { History, Clock, Search, Filter, X, AlertCircle, Loader2, User, LogIn, LogOut, Plus, Edit, Trash2, Eye, Printer, FileDown, Building2 } from 'lucide-react';
import logService from '../services/logs';
import bagianService from '../services/bagian';
import { useAuth } from '../context/AuthContext';

const ACTION_ICONS = {
  LOGIN: { icon: LogIn, color: 'text-green-600', bg: 'bg-green-50' },
  LOGOUT: { icon: LogOut, color: 'text-gray-600', bg: 'bg-gray-50' },
  CREATE: { icon: Plus, color: 'text-blue-600', bg: 'bg-blue-50' },
  UPDATE: { icon: Edit, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  DELETE: { icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
  VIEW: { icon: Eye, color: 'text-purple-600', bg: 'bg-purple-50' },
  PRINT: { icon: Printer, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  EXPORT: { icon: FileDown, color: 'text-teal-600', bg: 'bg-teal-50' },
};

const ACTION_FILTERS = [
  { value: 'All', label: 'All', icon: Filter, color: 'text-gray-600', bg: 'bg-gray-100' },
  { value: 'CREATE', label: 'Create', icon: Plus, color: 'text-blue-600', bg: 'bg-blue-50' },
  { value: 'UPDATE', label: 'Update', icon: Edit, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  { value: 'DELETE', label: 'Deletes', icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
  { value: 'LOGIN', label: 'Login', icon: LogIn, color: 'text-green-600', bg: 'bg-green-50' },
  { value: 'LOGOUT', label: 'Logout', icon: LogOut, color: 'text-gray-600', bg: 'bg-gray-50' },
  { value: 'VIEW', label: 'View', icon: Eye, color: 'text-purple-600', bg: 'bg-purple-50' },
  { value: 'PRINT', label: 'Print', icon: Printer, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  { value: 'EXPORT', label: 'Export', icon: FileDown, color: 'text-teal-600', bg: 'bg-teal-50' },
];

const MODULE_NAMES = {
  AUTH: 'Login/Auth', ACTIVITY: 'Activity', TICKET: 'Ticket',
  USER: 'User Management', BAGIAN: 'Master Bagian',
  FINANCE: 'Finance', AKUN: 'Master Akun', TRANSAKSI: 'Transaksi',
  JURNAL: 'Jurnal', LAPORAN: 'Laporan', HAK_AKSES: 'Hak Akses',
  OTHER: 'Other'
};

const LogHistories = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('All');
  const [filterAction, setFilterAction] = useState('All');
  const [filterBagian, setFilterBagian] = useState('All');
  const [bagianList, setBagianList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterModule, filterAction, filterBagian, dateRange]);

  // Fetch bagian list for admin filter
  useEffect(() => {
    if (isAdmin) {
      bagianService.getBagian().then(res => {
        const bagians = res.bagian || res || [];
        setBagianList(Array.isArray(bagians) ? bagians : []);
      }).catch(() => {});
    }
  }, [isAdmin]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };
      if (searchTerm) params.search = searchTerm;
      if (filterModule !== 'All') params.module = filterModule;
      if (filterAction !== 'All') params.action = filterAction;
      if (isAdmin && filterBagian !== 'All') params.bagian = filterBagian;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const data = await logService.getLogs(params);
      setLogs(data.logs || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, filterModule, filterAction, filterBagian, isAdmin, dateRange]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const getActionConfig = (action) => ACTION_ICONS[action] || { icon: Eye, color: 'text-gray-600', bg: 'bg-gray-50' };

  const activeActionLabel = filterAction === 'All' ? 'All Actions' : ACTION_FILTERS.find(a => a.value === filterAction)?.label || filterAction;

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Log Histories</h2>
          <p className="mt-1 text-sm text-gray-500">Complete activity log of all users in the system</p>
        </div>
        <div className="bg-blue-50 rounded-lg px-3 py-2">
          <span className="text-sm font-medium text-blue-700">{pagination?.totalItems || 0} total logs</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start space-x-2"><AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-red-700">{error}</p></div>
        </div>
      )}

      {/* Quick Action Filters */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3">
          <div className="flex items-center space-x-1.5 mb-1">
            <Filter className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Quick Filter by Action</span>
            {filterAction !== 'All' && (
              <button
                onClick={() => setFilterAction('All')}
                className="ml-auto text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear filter
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ACTION_FILTERS.map(action => {
              const isActive = filterAction === action.value;
              const ActionIcon = action.icon;
              return (
                <button
                  key={action.value}
                  onClick={() => setFilterAction(action.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    isActive
                      ? `${action.bg} ${action.color} ring-2 ring-offset-1 ${action.color.replace('text-', 'ring-')}`
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <ActionIcon className="w-3.5 h-3.5" />
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="px-4 py-3 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" placeholder="Search by user, description..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 hover:bg-white focus:bg-white" />
            {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"><X className="w-4 h-4" /></button>}
          </div>
          <div className={`grid grid-cols-2 ${isAdmin ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3`}>
            {isAdmin && (
              <select value={filterBagian} onChange={e => setFilterBagian(e.target.value)} className="select-field text-sm">
                <option value="All">All Bagian</option>
                <option value="Umum">Umum</option>
                {bagianList.map(b => (
                  <option key={b._id || b.name} value={b.name}>{b.name}</option>
                ))}
              </select>
            )}
            <select value={filterModule} onChange={e => setFilterModule(e.target.value)} className="select-field text-sm">
              <option value="All">All Modules</option>
              {Object.entries(MODULE_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="select-field text-sm">
              <option value="All">All Actions</option>
              {Object.keys(ACTION_ICONS).map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))}
              className="input-field text-sm" placeholder="Start date" />
            <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))}
              className="input-field text-sm" placeholder="End date" />
          </div>

          {/* Active filter indicator */}
          {filterAction !== 'All' && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
              <span>Currently showing:</span>
              <span className="font-semibold text-gray-700">{activeActionLabel}</span>
              <span>logs</span>
              <button
                onClick={() => setFilterAction('All')}
                className="ml-1.5 inline-flex items-center text-red-500 hover:text-red-700 font-medium"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Log List */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex flex-col items-center py-20"><Loader2 className="h-10 w-10 animate-spin text-blue-600" /><span className="mt-4 text-gray-500 font-medium">Loading logs...</span></div>
        ) : logs.length > 0 ? (
          logs.map((log, idx) => {
            const actionConfig = getActionConfig(log.action);
            const ActionIcon = actionConfig.icon;
            return (
              <div key={log._id || idx} className="card p-4 hover:shadow-md transition-all animate-slide-up" style={{ animationDelay: `${idx * 10}ms` }}>
                <div className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${actionConfig.bg} flex items-center justify-center`}>
                    <ActionIcon className={`w-5 h-5 ${actionConfig.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 text-xs text-gray-400">
                      <span className="font-mono font-semibold text-gray-700">{log.user?.username}</span>
                      {log.user?.bagian && <span className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{log.user.bagian}</span>}
                      {log.user?.name && <span className="text-gray-400">({log.user.name})</span>}
                      <span>•</span>
                      <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-medium">{MODULE_NAMES[log.module] || log.module}</span>
                      <span>•</span>
                      <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />{format(new Date(log.timestamp), 'dd MMM yyyy HH:mm:ss')}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{log.description}</p>
                    {log.detail && Object.keys(log.detail).length > 0 && (
                      <div className="mt-1 bg-gray-50 rounded-lg p-2 text-xs text-gray-500 font-mono max-h-20 overflow-auto">
                        {JSON.stringify(log.detail, null, 1).substring(0, 300)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="card p-12 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4"><History className="w-7 h-7 text-gray-400" /></div>
            <p className="text-gray-500 font-medium">No logs found</p>
            <p className="text-gray-400 text-sm mt-1">Logs will appear here as users perform activities</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between card px-4 py-3">
          <p className="text-sm text-gray-500">Page {pagination.currentPage} of {pagination.totalPages}</p>
          <div className="flex space-x-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-secondary text-xs disabled:opacity-50">Previous</button>
            <button disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)} className="btn-secondary text-xs disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogHistories;
